/**
 * Flight School course/progress hook — PWA port.
 *
 * iOS ref: client-native/src/features/flight-school/hooks/useFlightSchoolCourse.ts
 *
 * Reads the user's completed Flight School modules (GET /auth/flightschool/
 * progress) and exposes an optimistic `completeModule` (POST /auth/flightschool/
 * complete). Derives per-module status + graduation, matching iOS.
 */

import { useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { callWithAuth } from "@/api/Api";
import {
  getFlightSchoolProgress,
  postFlightSchoolModuleComplete,
} from "../lib/flightSchoolApi";
import {
  FLIGHT_SCHOOL_MODULES,
  FlightSchoolModule,
  FlightSchoolModuleStatus,
  deriveModuleStatuses,
  getAvailableModuleCount,
  getCurrentModuleId,
  hasGraduated as hasGraduatedFn,
} from "../lib/flightSchoolCourseData";
import { useFlightSchoolConfig } from "./useFlightSchoolConfig";

const FS_PROGRESS_QUERY_KEY = "flightSchoolProgress";

export interface UseFlightSchoolCourseReturn {
  modules: FlightSchoolModule[];
  completedModules: string[];
  statuses: Record<string, FlightSchoolModuleStatus>;
  currentModuleId: string;
  availableModuleCount: number;
  hasGraduated: boolean;
  isLoading: boolean;
  completeModule: (moduleId: string) => Promise<void>;
}

export function useFlightSchoolCourse(): UseFlightSchoolCourseReturn {
  const queryClient = useQueryClient();
  const { modules: configModules, isLoading: configLoading } =
    useFlightSchoolConfig();

  const { data, isLoading } = useQuery({
    queryKey: [FS_PROGRESS_QUERY_KEY],
    queryFn: () => callWithAuth(getFlightSchoolProgress),
    staleTime: 30_000,
  });

  const completedModules = data?.completedModules ?? [];

  // Backend-authored config (when published) OVERRIDES the hardcoded modules,
  // merging in any hardcoded ids it omits — exactly as iOS's useFlightSchoolCourse
  // does. With no active config the endpoint returns nothing and this collapses
  // to FLIGHT_SCHOOL_MODULES, so the common case is unchanged.
  const sourceModules = useMemo<FlightSchoolModule[]>(() => {
    if (configModules.length === 0) return FLIGHT_SCHOOL_MODULES;
    const configured: FlightSchoolModule[] = configModules.map((m) => ({
      id: m.id,
      title: m.title,
      subtitle: m.subtitle,
      type: "cognate",
    }));
    const configuredIds = new Set(configured.map((m) => m.id));
    const missingFallbacks = FLIGHT_SCHOOL_MODULES.filter(
      (m) => !configuredIds.has(m.id),
    );
    return [...configured, ...missingFallbacks];
  }, [configModules]);

  const completeModule = useCallback(
    async (moduleId: string) => {
      // Optimistic local update so the courses list reflects completion
      // immediately, even before the network round-trip resolves.
      queryClient.setQueryData(
        [FS_PROGRESS_QUERY_KEY],
        (prev: { completedModules: string[] } | undefined) => {
          const existing = prev?.completedModules ?? [];
          if (existing.includes(moduleId)) return prev ?? { completedModules: existing };
          return { completedModules: [...existing, moduleId] };
        },
      );

      try {
        await callWithAuth(postFlightSchoolModuleComplete, { moduleId });
      } finally {
        void queryClient.invalidateQueries({ queryKey: [FS_PROGRESS_QUERY_KEY] });
      }
    },
    [queryClient],
  );

  // Derive statuses / current / available-count / graduation from the merged
  // module list (config-overridden when published), matching iOS.
  return {
    modules: sourceModules,
    completedModules,
    statuses: deriveModuleStatuses(completedModules, sourceModules),
    currentModuleId: getCurrentModuleId(completedModules, sourceModules),
    availableModuleCount: getAvailableModuleCount(sourceModules),
    hasGraduated: hasGraduatedFn(completedModules, sourceModules),
    isLoading: isLoading || configLoading,
    completeModule,
  };
}
